<c-container fluid>
  <c-card class="mb-4">
    <c-card-header class="d-flex justify-content-between align-items-center flex-wrap gap-2">
      <div>
        <strong>{{ 'appointments.title' | translate }}</strong>
        <div class="small text-body-secondary">{{ 'appointments.subtitle' | translate }}</div>
      </div>
      @if (canManage()) {
        <a cButton color="primary" size="sm" [routerLink]="adminLink">
          {{ 'appointments.admin_menu' | translate }}
        </a>
      }
    </c-card-header>

    <c-card-body>
      @if (error()) {
        <div class="alert alert-danger py-2">{{ error() }}</div>
      }

      @if (loading()) {
        <div class="text-center py-4">
          <div class="spinner-border text-primary" role="status"></div>
        </div>
      } @else if (days().length === 0) {
        <p class="text-body-secondary mb-0">{{ 'appointments.empty' | translate }}</p>
        @if (canManage()) {
          <a cButton color="primary" size="sm" class="mt-3" [routerLink]="adminLink">
            {{ 'appointments.admin.new' | translate }}
          </a>
        }
      } @else {
        @for (day of days(); track day.key) {
          <div class="appointment-day">
            <h6 class="appointment-day__heading">
              {{ day.heading }}
              @if (day.isToday) {
                <c-badge color="primary" class="ms-2">{{ 'appointments.today' | translate }}</c-badge>
              } @else if (day.isTomorrow) {
                <c-badge color="secondary" class="ms-2">{{ 'appointments.tomorrow' | translate }}</c-badge>
              }
            </h6>

            @for (occurrence of day.occurrences; track occurrence.occurrence_id) {
              <div class="appointment" [class.appointment--cancelled]="occurrence.cancelled">
                <!-- The reader's own time. Never without its zone label. -->
                <div class="appointment__when">
                  <div class="appointment__time">
                    {{ localTime(occurrence) }}@if (localEndTime(occurrence)) {<span> – {{ localEndTime(occurrence) }}</span>}
                  </div>
                  <div class="appointment__zone">
                    {{ localZoneLabel(occurrence) }} · {{ 'appointments.your_time' | translate }}
                  </div>
                </div>

                <div class="appointment__body">
                  <div class="appointment__title">
                    <span class="appointment__title-text">{{ occurrence.title }}</span>
                    @if (occurrence.cancelled) {
                      <c-badge color="danger" class="ms-2">{{ 'appointments.cancelled' | translate }}</c-badge>
                    }
                    @if (occurrence.recurring) {
                      <c-badge color="info" class="ms-2">
                        {{ 'appointments.recurrence.' + occurrence.recurrence_rule | translate }}
                      </c-badge>
                    }
                    @if (occurrence.internal_only) {
                      <c-badge color="dark" class="ms-2">{{ 'appointments.internal' | translate }}</c-badge>
                    }
                  </div>

                  @if (occurrence.cancelled) {
                    <div class="appointment__cancelled-note">
                      {{ (occurrence.cancellation_note || ('appointments.cancelled_hint' | translate)) }}
                    </div>
                  }

                  <!-- Who it is meant for: compact role labels, never a member list. -->
                  @if (occurrence.audience_labels?.length) {
                    <div class="appointment__audiences">
                      @for (label of occurrence.audience_labels; track label.key) {
                        <!-- `member` is everyone; a label that is on every appointment says nothing. -->
                        @if (label.key !== 'member') {
                          <span class="appointment__audience">{{ label.name }}</span>
                        }
                      }
                    </div>
                  }

                  <!--
                    The appointment's own zone, always spelled out — unless the
                    reader is already in it, where repeating the same clock time
                    twice only makes the entry harder to read.
                  -->
                  @if (!sameZoneAsReader(occurrence)) {
                    <div class="appointment__origin">
                      {{ occurrence.local_time }}@if (occurrence.local_end_time) {<span> – {{ occurrence.local_end_time }}</span>}
                      {{ occurrence.time_zone_abbreviation }}
                      ({{ occurrence.time_zone }}) · {{ 'appointments.event_time_zone' | translate }}
                    </div>
                  } @else {
                    <div class="appointment__origin">
                      {{ occurrence.time_zone_abbreviation }} ({{ occurrence.time_zone }}) ·
                      {{ 'appointments.event_time_zone' | translate }}
                    </div>
                  }

                  @if (occurrence.location) {
                    <div class="appointment__location">
                      {{ 'appointments.location' | translate }}: {{ occurrence.location }}
                    </div>
                  }

                  @if (occurrence.details) {
                    <p class="appointment__details">{{ occurrence.details }}</p>
                  }

                  <!-- A cancelled occurrence offers no way in: the link may still work, the call will not happen. -->
                  @if (occurrence.join_url && !occurrence.cancelled) {
                    <a cButton color="primary" size="sm" class="appointment__join"
                       [href]="occurrence.join_url" target="_blank" rel="noopener noreferrer">
                      {{ 'appointments.join' | translate }}
                    </a>
                  }
                </div>
              </div>
            }
          </div>
        }
      }
    </c-card-body>
  </c-card>
</c-container>
